









































import {
  Editor,
  Extension,
  Mark,
  Node,
  Content,
  JSONContent
} from '@tiptap/vue-2'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import CRichTextEditorMenuBar from '~/components/shared/configurable/form/rich-text/CRichTextEditorMenuBar.vue'
import CTextArea from '~/components/shared/configurable/form/input/CTextArea.vue'
import CRichTextEditorTextArea from '~/components/shared/configurable/form/rich-text/CRichTextEditorTextArea.vue'
import {
  defineComponent,
  PropType,
  onMounted,
  onBeforeUnmount,
  ref,
  computed,
  watch
} from '~/utils/nuxt3-migration'
import { toolbarConfig } from './config'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'
import { useLogger } from '~/compositions/logger'

interface RichTextEditorProps {
  extensions: (Mark | Node | Extension)[]
  content: string | Content | JSONContent
  state: boolean | null
  hasError: boolean
  name: string | undefined
  textareaPlaceholder: string
  useBubbleMenu: boolean
  editorMinHeightClass: string
  editorMaxHeightClass: string
}

export default defineComponent({
  components: {
    CTextArea,
    CRichTextEditorTextArea,
    CRichTextEditorMenuBar
  },
  props: {
    // https://v2.tiptap.dev/docs/editor/extensions/overview
    extensions: {
      type: Array,
      required: true,
      default() {
        return [] as (Mark | Node | Extension)[]
      }
    },
    content: {
      type: [Object, String, null] as PropType<
        string | Content | JSONContent | null
      >,
      default: null
    },
    state: {
      type: Boolean,
      default: null
    },
    hasError: {
      type: Boolean,
      default: false
    },
    textareaPlaceholder: {
      type: String,
      default: ''
    },
    useBubbleMenu: {
      type: Boolean,
      default: false
    },
    editorMinHeightClass: {
      type: String,
      default: '!tw-min-h-72'
    },
    editorMaxHeightClass: {
      type: String,
      default: '!tw-max-h-[500px]'
    },
    name: {
      type: String,
      default: undefined
    }
  },

  setup(props: RichTextEditorProps, { emit }) {
    const { t } = useI18n()
    const logger = useLogger()

    const editor = ref<Editor | null>(null)
    const isEditorLoaded = ref(false)
    const currentExtensions = ref<(Mark | Node | Extension)[]>([])

    onMounted(() => {
      initializeEditor()
    })

    onBeforeUnmount(() => {
      if (editor.value) editor.value.destroy()
    })

    const toolbarGroups = computed(() => {
      return toolbarConfig
        .filter(
          oe =>
            oe.inMenu &&
            props.extensions.find(
              (e: Mark | Node | Extension) => e.name === oe.name
            )
        )
        .map(oe => ({
          ...oe,
          commands: oe.commands.map(command => ({
            ...command,
            translation: {
              key: command.translation.key,
              text: t(command.translation.key) as string
            }
          }))
        }))
    })

    const placeholder = computed(() => {
      return props.textareaPlaceholder || (t('all') as string)
    })

    const contentValue = computed(() => {
      return typeof props.content === 'string'
        ? props.content
        : props.content
        ? JSON.stringify(props.content)
        : ''
    })

    watch(
      () => props.content,
      content => {
        if (!editor.value) return
        if (content) editor.value.commands.setContent(content)
        else editor.value.commands.clearContent()
      }
    )

    async function initializeEditor() {
      isEditorLoaded.value = false
      try {
        currentExtensions.value = await getExtensions()
        editor.value = new Editor({
          onUpdate: handleEditorUpdate,
          content: props.content,
          extensions: currentExtensions.value,
          editorProps: {
            attributes: {
              class: `${props.editorMinHeightClass}  ${props.editorMaxHeightClass}`
            }
          }
        })
      } catch (e) {
        logger.captureError(e)
      } finally {
        isEditorLoaded.value = true
      }
    }

    function getExtensions(): (Mark | Node | Extension)[] {
      const defaultExtensions = [Document, Paragraph, Text]
      return [...defaultExtensions, ...props.extensions]
    }

    function handleEditorUpdate() {
      if (!editor.value) return
      let html = editor.value.getHTML()
      let json = editor.value.getJSON()

      if (
        JSON.stringify(json) ===
        JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] })
      ) {
        json = {}
      }

      if (html === '<p></p>') html = ''

      emit('input', {
        json,
        html,
        text: editor.value.state.doc.textContent
      })
    }

    function onTextareaInput(value: string) {
      emit('input', {
        json: value,
        html: null,
        text: value
      })
    }

    return {
      editor,
      isEditorLoaded,
      toolbarGroups,
      onTextareaInput,
      placeholder,
      contentValue
    }
  },
  i18n: defineComponentTranslations({
    redo: {
      en: 'Redo',
      el: 'Επανάληψη'
    },
    undo: {
      en: 'Undo',
      el: 'Αναίρεση'
    },
    heading_1: {
      en: 'Heading 1',
      el: 'Επικεφαλίδα 1'
    },
    heading_2: {
      en: 'Heading 2',
      el: 'Επικεφαλίδα 2'
    },
    heading_3: {
      en: 'Subtitle',
      el: 'Υπότιτλος'
    },
    bold: {
      en: 'Bold',
      el: 'Έντονα γράμματα'
    },
    italic: {
      en: 'Italic',
      el: 'Πλάγια γράμματα'
    },
    underline: {
      en: 'Underline',
      el: 'Υπογράμμιση'
    },
    align_left: {
      en: 'Align left',
      el: 'Αριστερή στοίχιση'
    },
    align_center: {
      en: 'Align center',
      el: 'Κεντρική στοίχιση'
    },
    align_right: {
      en: 'Align right',
      el: 'Δεξιά στοίχιση'
    },
    bullet_list: {
      en: 'Bullet list',
      el: 'Λίστα'
    },
    ordered_list: {
      en: 'Ordered list',
      el: 'Αριθμημένη λίστα'
    },
    image: {
      en: 'Add image',
      el: 'Προσθήκη εικόνας'
    },
    link: {
      en: 'Add link',
      el: 'Προσθήκη συνδέσμου'
    }
  })
})
